import { Injectable } from "@angular/core";
import { BaseEntityService } from "../rest-common.service";
import { TdspCreativeService } from "./tdsp-creative.service";
import { Router } from "@angular/router";
import {
  doohCrtvTypes,
  nonImgCrtv,
  tdspNativeTypes,
} from "src/app/models/tdsp/creatives";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { TdspPixelService } from "./tdsp-pixel.service";
import { callback } from "src/app/shared/utils/commonUtils";
import {
  calculateDateTime,
  getDateMonthYear,
  getDateObjectfromString,
  getDateTime,
  getNowInTimezone,
  getTodayDate,
} from "src/app/shared/utils/dateUtil";
import {
  awareness,
  consideration,
  conversion,
  currencyMetrics,
  Goal,
  ioFlightsRetrivalData,
  KpiItem,
  nonValueMetrics,
  TdspIO,
  TdspIoAssoc,
} from "src/app/models/tdsp/tdsp-insertion-order";
import { extractKeys } from "src/app/shared/utils/arrayUtils";
import { TdspInsertionOrderService } from "./tdsp-insertion-order.service";
import { CommonService } from "../common.service";
import { TdspCustomLocationService } from "./tdsp-custom-location.service";
import { TdspContextualCategoryService } from "./tdsp-contextual-category.service";
import { TdspLineItemService } from "./tdsp-line-item.service";
import { TdspConversionPixelService } from "./tdsp-conversion-pixel.service";

@Injectable({
  providedIn: "root",
})
export class TdspCommonService extends BaseEntityService {
  constructor(
    private creativeService: TdspCreativeService,
    private pixelService: TdspPixelService,
    private ioService: TdspInsertionOrderService,
    private commonService: CommonService,
    private tdspCustomLocationService: TdspCustomLocationService,
    private tdspLineItemService: TdspLineItemService,
    private tdspContextualCategoryService: TdspContextualCategoryService,
    private tdspConversionPixelService: TdspConversionPixelService,
    private router: Router
  ) {
    super();
  }

  prepareAndSubmitCreative(
    event: any,
    mode: "create" | "update",
    globalParams: Object,
    showThirdPartyPixel: boolean,
    creativeId?: number
  ): void {
    const creativeData = JSON.parse(JSON.stringify(event.creative));
    const adType = creativeData.ad_type;
    const isThirdParty = event.creative.upload_type === "tag";

    setTimeout(() => {
      // Clean up creative data
      delete creativeData.upload_type;

      creativeData.folder =
        creativeData.folder == 0 ? null : creativeData.folder;

      if (!tdspNativeTypes.has(creativeData.ad_type)) {
        creativeData.native_attributes = null;
      }

      if (creativeData.ad_type != "html5") {
        creativeData.additional_details = null;
      }

      if (nonImgCrtv.has(creativeData.ad_type)) {
        creativeData.companion_creative_ids = [
          creativeData.companion_creative_ids,
        ];
        creativeData.companion_creative_ids =
          creativeData.companion_creative_ids.filter((ids) => ids != "");
        creativeData.image_pixels = null;
        creativeData.javascript_tags = null;
        if (!showThirdPartyPixel) {
          creativeData.events.length = 0;
        }
      } else {
        creativeData.events = null;
        creativeData.is_retargeting_enabled = false;
        creativeData.companion_creative_ids = null;
        creativeData.javascript_tags = JSON.parse(
          JSON.stringify(
            creativeData.third_party_pixels.map((pixel) => pixel.url)
          )
        );
        delete creativeData.third_party_pixels;
      }

      if (!doohCrtvTypes.has(creativeData.ad_type)) {
        creativeData.viooh_inventory = null;
      }

      if (isThirdParty) {
        creativeData.click_through_url = null;
      } else {
        creativeData.third_party_tag = null;
      }

      // Prepare FormData
      const formData = new FormData();

      // File attachment based on adType
      if (mode == "create") {
        // Creative file
        formData.append("file", event.file);

        if (tdspNativeTypes.has(adType)) {
          // Logo & Icon
          if (event.icon) formData.append("icon", event.icon);
          if (event.logo) formData.append("logo", event.logo);
        }

        // Native Video

        if (creativeData.ad_type == "native_video" && event.nativeMainImg) {
          formData.append("image", event.nativeMainImg);
        }
      }

      // Append creative JSON
      formData.append("creative", JSON.stringify(creativeData));

      // Call appropriate service
      const callback = () => {
        this.router.navigate(["/tdsp/creative"], {
          queryParams: { ...globalParams },
        });
      };

      if (mode === "create") {
        this.creativeService.create(formData, globalParams).subscribe(
          () => {},
          () => {},
          callback
        );
      } else {
        this.creativeService
          .update(creativeId!, formData, globalParams)
          .subscribe(
            callback,
            () => {},
            () => {}
          );
      }
    });
  }

  preparePayloadAndSubmitPixel(
    event: any,
    mode: "create" | "update",
    globalParams: Object,
    pixelId?: number
  ): void {
    const pixel = JSON.parse(JSON.stringify(event.pixel));

    delete pixel.containsParam;

    pixel.mappings.forEach((mapping) => {
      if (mapping.type === "exact") {
        mapping.url_pattern = null;
      } else {
        mapping.url = null;
      }
    });

    if (mode === "create") {
      this.pixelService.create(pixel, globalParams).subscribe(
        () => {},
        () => {},
        () => callback(this.router, "/tdsp/segments", globalParams)
      );
    } else {
      this.pixelService.update(pixelId, pixel, globalParams).subscribe(
        () => {},
        () => {},
        () => callback(this.router, "/tdsp/segments", globalParams)
      );
    }
  }

  preparePayloadAndSubmitConversionPixel(
    event: any,
    mode: "create" | "update",
    globalParams: Object,
    pixelId?: number
  ): void {
    const conversionPixel = JSON.parse(JSON.stringify(event.conversionPixel));

    delete conversionPixel.containsParam;

    if (mode === "create") {
      this.tdspConversionPixelService
        .create(conversionPixel, globalParams)
        .subscribe(
          () => {},
          () => {},
          () => callback(this.router, "/tdsp/conversion-pixel", globalParams)
        );
    } else {
      this.tdspConversionPixelService
        .update(pixelId, conversionPixel, globalParams)
        .subscribe(
          () => {},
          () => {},
          () => callback(this.router, "/tdsp/conversion-pixel", globalParams)
        );
    }
  }

  setStartAndEndDate(
    tdspIO: TdspIO,
    timeZone: string,
    isEdit: boolean = false
  ) {
    let today = new Date(getTodayDate(timeZone));
    today.setHours(0, 0, 0, 0);
    tdspIO.flights.forEach((flight) => {
      if (flight.start_date) {
        flight.start_date = getDateObjectfromString(flight.start_date);
        flight.disable_start_date = flight.start_date <= today && isEdit;
      }
      if (flight.end_date) {
        flight.end_date = getDateObjectfromString(flight.end_date);
        flight.disable_end_date = flight.end_date <= today && isEdit;
      }
    });
  }

  transformKpiPayload(
    kpis: KpiItem[],
    type: "primary" | "secondary" | "tertiary"
  ) {
    const kpi = kpis.find((k) => k.type === type);
    if (!kpi) return null;

    return {
      goal_type: kpi.goal_type,
      value: kpi.showValue ? Number(kpi.value) : true,
    };
  }

  formatGoals(io: TdspIO): KpiItem[] {
    const goalOrder: {
      key: keyof TdspIO;
      title: string;
      type: "primary" | "secondary" | "tertiary";
    }[] = [
      { key: "primary_goal", title: "PRIMARY KPI", type: "primary" },
      { key: "secondary_goal", title: "SECONDARY KPI", type: "secondary" },
      { key: "tertiary_goal", title: "TERTIARY KPI", type: "tertiary" },
    ];

    const isCurrencyMetric = (goalType: string): boolean => {
      return currencyMetrics.has(goalType.toLowerCase());
    };

    const isValueMetric = (goalType: string): boolean => {
      return !nonValueMetrics.has(goalType.toLowerCase());
    };

    return goalOrder.flatMap(({ key, title, type }) => {
      const goal = io[key] as Goal | null;
      if (!goal) return [];

      return [
        {
          title,
          name: goal.goal_type.toUpperCase(),
          value: goal.value === null ? "" : goal.value,
          goal_type: goal.goal_type,
          type,
          showValue: isValueMetric(goal.goal_type),
          isCurrency: isCurrencyMetric(goal.goal_type),
        } as KpiItem,
      ];
    });
  }

  mapBudgetInterval(tdspIo: TdspIO) {
    tdspIo.flights.forEach((flight) => {
      if (tdspIo.budget_type == "impression") {
        flight.revenue_budget = null;
        flight.daily_revenue_budget = null;
        if (tdspIo.pacing != "pace_daily") {
          flight.daily_imps_budget = null;
        }
      } else {
        flight.impression_budget = null;
        flight.daily_imps_budget = null;
        if (tdspIo.pacing != "pace_daily") {
          flight.daily_revenue_budget = null;
        }
      }
      if (flight.start_date != null) {
        flight.start_date = `${calculateDateTime(
          new Date(flight.start_date),
          "ioPayload"
        )}`;
      }

      if (flight.end_date != null) {
        flight.end_date = `${getDateMonthYear(
          new Date(flight.end_date)
        )} 23:59:59`;
      }
      delete flight.disable_start_date;
      delete flight.disable_end_date;
    });
  }

  hasConversions(selectedKpis: Array<any>) {
    return selectedKpis.some((kpi) => conversion.has(kpi));
  }

  mapTrackingTagsToAPI(tracking_tags, type: string) {
    return tracking_tags
      .filter((tag) => tag.type == type)
      .map(({ id, priority, ...rest }) => ({
        id,
        priority,
      }));
  }

  mapTrackingTagsToUI(tracking_tags, type, duplicate) {
    if (type == "universal-pixel") {
      return tracking_tags.map(({ priority, mapping }) => ({
        priority,
        type,
        ...mapping,
        new: duplicate,
      }));
    } else {
      return tracking_tags.map(({ priority, image_pixel }) => ({
        priority,
        type,
        ...image_pixel,
        new: duplicate,
      }));
    }
  }

  async getDataAndWait(request) {
    return await request.toPromise();
  }

  async activateDeactivateLIs(action, aliIds, globalParams) {
    if (!aliIds?.length) return;

    const promises = aliIds.map((id) =>
      this.tdspLineItemService
        .activateDeactiveLineItem(id, action, globalParams)
        .toPromise()
    );

    await Promise.all(promises);

    this.router.navigate(["/tdsp/insertion-order"], {
      queryParams: globalParams,
    });
  }

  prepareIoPayloadAndSubmit(
    tdspIo: TdspIO,
    kpi: Array<KpiItem>,
    tdspIoAssoc: TdspIoAssoc,
    mode: "create" | "update",
    globalParams: Object,
    ioId?: number,
    removedLiGoalIds?: Array<any>
  ) {
    const ioPayload = JSON.parse(JSON.stringify(tdspIo));
    const kpis = JSON.parse(JSON.stringify(kpi));

    ioPayload.flights = ioPayload.flights.map(
      ({
        click_pacing_average_daily_budget,
        click_pacing_clicks_delivered,
        ...rest
      }) => rest
    );

    ioPayload.primary_goal = this.transformKpiPayload(kpis, "primary");
    ioPayload.secondary_goal = this.transformKpiPayload(kpis, "secondary");
    ioPayload.tertiary_goal = this.transformKpiPayload(kpis, "tertiary");

    ioPayload.seed = extractKeys(tdspIoAssoc.seed)[0];

    ioPayload.pixel_mappings = this.mapTrackingTagsToAPI(
      JSON.parse(JSON.stringify(tdspIoAssoc.tracking_tags)),
      "universal-pixel"
    );

    ioPayload.image_pixels = this.mapTrackingTagsToAPI(
      JSON.parse(JSON.stringify(tdspIoAssoc.tracking_tags)),
      "conversion-pixel"
    );

    this.mapBudgetInterval(ioPayload);

    if (mode == "create") {
      this.ioService.create(ioPayload, globalParams).subscribe(
        (res) => {},
        (err) => {},
        () => callback(this.router, "/tdsp/insertion-order", globalParams)
      );
    } else {
      this.ioService.update(ioId, ioPayload, globalParams).subscribe(
        (res) => {},
        (err) => {},
        () => {
          if (removedLiGoalIds.length > 0) {
            this.activateDeactivateLIs(
              "deactivate",
              removedLiGoalIds,
              globalParams
            );
          } else {
            callback(this.router, "/tdsp/insertion-order", globalParams);
          }
        }
      );
    }
  }

  calculateAvgClickDailyBudget(flight): number {
    const start = new Date(flight.start_date).getTime();
    const end = new Date(flight.end_date).getTime();

    const flightTotalSeconds = (end - start) / 1000;

    const remainingDays = Math.ceil(flightTotalSeconds / 86400);

    return Math.floor(flight.click_pacing_flight_budget / remainingDays);
  }

  hasKPI(kpis: Array<any>, type) {
    const kpiGroups = {
      awareness,
      consideration,
      conversion,
    } as const;

    return kpis.some((kpi) => kpiGroups[type]?.has(kpi.goal_type));
  }

  prepareAndSubmitCustomLocation(
    custLocation,
    mapLocations,
    mode: "create",
    globalParams: Object
  ) {
    const payload = JSON.parse(JSON.stringify(custLocation));
    const locations = JSON.parse(JSON.stringify(mapLocations));

    payload.points = locations.map((item) => ({
      lat_deg: String(item.lat),
      lng_deg: String(item.lng),
      rad_met: item.radius,
      name: item.name,
    }));

    if (mode == "create") {
      this.tdspCustomLocationService.create(payload, globalParams).subscribe(
        () => {},
        () => {},
        () => callback(this.router, "/tdsp/custom-location", globalParams)
      );
    }
  }

  preparePayloadAndSubmitCntCtg(
    cntCtg,
    keyWordMappings,
    agencies,
    advertisers,
    agencyAdvertisers,
    mode: "create" | "update",
    globalParams: Object,
    cntCtgId?: number
  ) {
    cntCtg.keywords.length = 0;
    Object.keys(keyWordMappings).forEach((key) => {
      const [match_application_type, match_type] = key.split("_");

      let values = keyWordMappings[key];
      if (!Array.isArray(values)) {
        values =
          typeof values === "string" && values.length > 0
            ? values
                .split(",")
                .map((v) => v.trim())
                .filter((v) => v.length > 0)
            : [];
      }

      values.forEach((value) => {
        cntCtg.keywords.push({
          match_application_type,
          match_type,
          value,
        });
      });
    });

    if (!cntCtg.available_to_all) {
      cntCtg.advertisers = advertisers.map((ele) => ele.id);
      cntCtg.agencies = agencies.map((ele) => ele.id);
      cntCtg.agency_advertisers = agencyAdvertisers.map((ele) => ele.id);
    } else {
      cntCtg.agencies = [];
      cntCtg.advertisers = [];
      cntCtg.agency_advertisers = [];
    }

    if (mode == "create") {
      this.tdspContextualCategoryService.create(cntCtg, globalParams).subscribe(
        () => {},
        () => {},
        () => callback(this.router, "/tdsp/contextual-keywords", globalParams)
      );
    } else {
      this.tdspContextualCategoryService
        .update(cntCtgId, cntCtg, globalParams)
        .subscribe(
          () => {},
          () => {},
          () => callback(this.router, "/tdsp/contextual-keywords", globalParams)
        );
    }
  }
}

@Injectable({
  providedIn: "root",
})
export class TdspCommonAPIService extends BaseEntityService {
  constructor(private tdspCreativeService: TdspCreativeService) {
    super();
  }

  getSupportedCrtvSizes(params): Observable<any[]> {
    return this.tdspCreativeService
      .getCreativeSizes(params)
      .pipe(map((res) => res.data || []));
  }
}
